import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  CheckCircle,
  DollarSign,
  Shield,
  TrendingUp,
  Users,
} from "lucide-react";
import SpaceJetNavbar from "../components/SpaceJetNavbar";
import SpaceJetParticleField from "../components/SpaceJetParticleField";
import Reveal from "../components/Reveal";
import { ThemeProvider } from "../contexts/ThemeContext";

const SpaceJetNetworkAffiliates = () => {
  const benefits = [
    {
      icon: DollarSign,
      title: "Better offers",
      description: "Access selective offers with stronger fit, better conversion potential, and competitive payouts.",
    },
    {
      icon: TrendingUp,
      title: "Clear performance insight",
      description: "Track results with straightforward analytics and real-time conversion visibility.",
    },
    {
      icon: Users,
      title: "Hands-on publisher support",
      description: "Get direct help from a team that works with you to improve results.",
    },
    {
      icon: Shield,
      title: "Reliable payouts",
      description: "Clear reporting, dependable payments, and fewer surprises on the back end.",
    },
  ];

  const features = [
    "Weekly payment schedule",
    "Multiple payout methods",
    "Clear tracking and reporting",
    "Selective offer access",
    "Offer matching support",
    "API integration available",
    "Performance insight from the team",
    "Direct publisher support",
  ];

  const signals = [
    { label: "Network", value: "Selective fit", icon: Users },
    { label: "Offers", value: "Better matched", icon: DollarSign },
    { label: "Support", value: "Hands-on", icon: TrendingUp },
    { label: "Payments", value: "Reliable", icon: BarChart3 },
  ];

  const workingStyle = [
    {
      title: "Offer fit",
      text: "We look for offers that respect your traffic and have a real chance to convert.",
    },
    {
      title: "Direct communication",
      text: "You get clear answers on terms, tracking, and what to try next when performance shifts.",
    },
    {
      title: "Revenue focus",
      text: "The goal is simple: help you build revenue with fewer dead ends and better support.",
    },
  ];

  return (
    <ThemeProvider>
      <div
        className="min-h-screen bg-[#050b17] text-slate-100 spacejet-texture-dots spacejet-texture-living transition-colors duration-300"
        style={{
          "--spacejet-texture-dot-opacity": 0.105,
          "--spacejet-texture-grid-opacity": 0.07,
          "--spacejet-texture-ambient-opacity": 0.085,
          "--spacejet-texture-drift-duration": "50s",
          "--spacejet-texture-glow-duration": "35s",
          "--spacejet-texture-sheen-duration": "41s",
        }}
      >
        <SpaceJetNavbar />
        <SpaceJetParticleField maxParticles={56} speed={0.15} baseOpacity={0.21} />

        <section className="relative overflow-hidden py-20 md:py-24">
          <div className="absolute inset-0 spacejet-gradient-overlay" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <Reveal animation="spacejet-fade-up">
                <div className="spacejet-page-kicker mb-6">
                  Publishers
                </div>
                <h1 className="spacejet-page-title text-4xl font-semibold leading-tight text-slate-100 md:text-6xl">
                  Join a selective network
                  <span className="block font-light text-slate-300">built for revenue.</span>
                </h1>
              </Reveal>

              <Reveal animation="spacejet-fade-up" delay={100}>
                <p className="spacejet-page-copy mb-10 max-w-3xl text-xl">
                  SpaceJet gives publishers better offers, clear reporting, and hands-on support so you can grow revenue with partners that fit your traffic.
                </p>
              </Reveal>

              <Reveal animation="spacejet-fade-up" delay={200}>
                <div className="flex flex-col gap-4 sm:flex-row">
                  <Link to="/signup" className="spacejet-btn-primary group inline-flex items-center justify-center rounded-lg px-8 py-4 font-semibold transition-colors duration-200">
                    Apply as a publisher
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                  <Link to="/contact" className="spacejet-btn-secondary inline-flex items-center justify-center rounded-lg px-8 py-4 font-semibold transition-colors duration-200 hover:bg-white/[0.04]">
                    Talk to the team
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
              {signals.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Reveal key={item.label} animation="spacejet-scale-in" delay={index * 100}>
                    <div className="spacejet-surface-card rounded-2xl p-5 text-left">
                      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-slate-600 to-slate-700">
                        <Icon className="h-6 w-6 text-slate-200" />
                      </div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.32em] text-slate-400">{item.label}</div>
                      <div className="mt-3 text-xl font-semibold text-slate-100">{item.value}</div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <section className="relative py-24">
          <div className="absolute inset-0 spacejet-texture-grid opacity-30" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal animation="spacejet-fade-up">
              <div className="mb-16 text-center">
                <div className="spacejet-page-kicker mb-4 justify-center">Why publishers choose SpaceJet</div>
                <h2 className="spacejet-page-title text-4xl font-semibold text-slate-100 md:text-5xl">
                  More useful offers, clearer guidance, and reliable payouts.
                </h2>
                <p className="spacejet-page-copy mx-auto mt-6 max-w-3xl text-xl">
                  We focus on selective partnerships, stronger offers, faster support, and a payment flow you can count on.
                </p>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <Reveal key={benefit.title} animation="spacejet-scale-in" delay={index * 120}>
                    <div className="group spacejet-surface-card spacejet-hover-lift rounded-2xl p-8 hover:bg-slate-800/40 transition-colors duration-300">
                      <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-r from-slate-600 to-slate-700 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="h-8 w-8 text-slate-200" />
                      </div>
                      <h3 className="mb-4 text-2xl font-bold text-slate-100">{benefit.title}</h3>
                      <p className="leading-relaxed text-slate-400">{benefit.description}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-24 spacejet-glass-subtle">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-start">
              <div>
                <div className="spacejet-page-kicker mb-4">Managed support</div>
                <h2 className="spacejet-page-title mb-6 text-4xl font-semibold text-slate-100">
                  What publishers get from the partnership.
                </h2>
                <p className="spacejet-page-copy mb-8 text-xl">
                  SpaceJet is built for publishers who want better access, clearer reporting, and direct support from the team.
                </p>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {features.map((feature) => (
                    <div key={feature} className="flex items-start rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
                      <CheckCircle className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-400" />
                      <span className="text-slate-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="spacejet-surface-card rounded-2xl p-8">
                <div className="mb-6 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-100">Publisher experience</h3>
                  <div className="flex space-x-2">
                    <div className="spacejet-accent-dot h-3 w-3 rounded-full" />
                    <div className="h-3 w-3 rounded-full bg-yellow-500" />
                    <div className="h-3 w-3 rounded-full bg-green-500" />
                  </div>
                </div>

                <div className="space-y-4">
                  {workingStyle.map((item, index) => (
                    <div key={item.title} className="flex gap-4 rounded-lg bg-slate-800/55 p-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-red-500/10 text-[11px] font-semibold uppercase tracking-[0.28em] text-red-200">
                        0{index + 1}
                      </div>
                      <div>
                        <div className="text-base font-semibold text-slate-100">{item.title}</div>
                        <p className="mt-1 text-sm leading-7 text-slate-300">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <div className="spacejet-page-kicker mb-4 justify-center">Next step</div>
            <h2 className="spacejet-page-title mb-6 text-4xl font-semibold text-slate-100 md:text-5xl">
              Ready to apply to the network?
            </h2>
            <p className="spacejet-page-copy mb-12 text-xl">
              Join publishers who want selective offers, direct support, and a stronger path to revenue.
            </p>

            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link to="/signup" className="spacejet-btn-primary group inline-flex items-center justify-center rounded-lg px-10 py-5 font-bold transition-colors duration-200">
                Apply to join
                <ArrowRight className="ml-3 h-6 w-6 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/contact" className="spacejet-btn-secondary inline-flex items-center justify-center rounded-lg px-10 py-5 font-bold transition-colors duration-200 hover:bg-white/[0.04]">
                Contact the team
              </Link>
            </div>
          </div>
        </section>
      </div>
    </ThemeProvider>
  );
};

export default SpaceJetNetworkAffiliates;
